import React, { createContext, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from './useLocalStorage';
import { User } from '../types/user';
import { AuthService } from '../openapi/services/AuthService';
import { ApiError, OpenAPI } from '../openapi';
import { ResponseDTO } from '../openapi/models/ResponseDTO';
import { SignInDTO } from '../openapi/models/SignInDTO';

interface Props {
  children: ReactNode;
}

interface AuthContextType {
  user: User | null;
  login: (data: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [user, setUser] = useLocalStorage<User | null>('user', null);
  const navigate = useNavigate();

  const login = async (data: User) => {
    await AuthService.signIn(data.partnerId, data.token!)
      .then((response) => {
        setUser({
          partnerId: data.partnerId,
          accessToken: response.data?.access_token,
        });
        OpenAPI.TOKEN = response.data?.access_token;
        navigate('/', { replace: true });
      })
      .catch((error: ApiError) => {
        const response = error.body as ResponseDTO<SignInDTO>;
        alert(response.message);
      });
  };

  const logout = () => {
    setUser(null);
    navigate('/signIn', { replace: true });
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
